import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateBankCostLookupListFormValues } from '../../../../../interfaces/lookup/bankCost';
import { IUpdateBankCostOption } from '../../../../../interfaces/lookup/bankCost/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBankCostOptionById, updateBankCostOption } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BankCostLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateBankCostLookupListFormValues) => {
        const body: IUpdateBankCostOption = {
            id: lookupSlice.detailsPage.bankCostOption.data?.id,
            ...(values as any),
        };

        dispatch(updateBankCostOption(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.bankCostOption.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/bankcost', { replace: true });
        }
    }, [lookupSlice.detailsPage.bankCostOption.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBankCostOptionById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.bankCostOption.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.bankCostOption.data.id,
                code: lookupSlice.detailsPage.bankCostOption.data.code,
                description: lookupSlice.detailsPage.bankCostOption.data.description,
            } as IUpdateBankCostLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.bankCostOption.data]);

    if (lookupSlice.detailsPage.bankCostOption.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="bankcost-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage bank cost lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Bank cost',
                        path: '/admin/lookup-lists/bankcost',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.bankCostOption.data?.code || '-',
                        path: `/admin/lookup-lists/bankcost/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/bankcost/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.bankCostOption.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.bankCostOption.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankCostOption.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.bankCostOption.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.bankCostOption.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.bankCostOption.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.bankCostOption.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.bankCostOption.fetchStatus} about={'Bank cost'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Bank cost option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.bankCostOption?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankCostOption.data?.created
                                            ? dayjs(lookupSlice.detailsPage.bankCostOption.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.bankCostOption.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.bankCostOption.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.bankCostOption.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
